import React, { Component, ErrorInfo, ReactNode } from 'react';

import { logger } from '@/utils/logger';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with full context
    logger.error(
      `Error Boundary caught error: ${error.message}`,
      'ErrorBoundary',
      {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        errorInfo: {
          componentStack: errorInfo.componentStack,
        },
        timestamp: new Date().toISOString(),
      }
    );

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info for potential display
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className='min-h-screen flex items-center justify-center bg-background'>
          <div className='max-w-md mx-auto text-center p-6'>
            <div className='mb-4'>
              <h1 className='text-2xl font-bold text-destructive mb-2'>
                Something went wrong
              </h1>
              <p className='text-muted-foreground'>
                An unexpected error occurred. Please refresh the page or try
                again.
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className='mt-4 p-4 bg-secondary rounded-lg text-left'>
                <h3 className='font-semibold mb-2'>
                  Error Details (Development Only):
                </h3>
                <p className='text-sm font-mono text-destructive mb-2'>
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <pre className='text-xs text-muted-foreground overflow-auto max-h-40'>
                    {this.state.error.stack}
                  </pre>
                )}
              </div>
            )}

            <div className='mt-6 space-x-4'>
              <button
                onClick={() => window.location.reload()}
                className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90'
              >
                Refresh Page
              </button>
              <button
                onClick={() =>
                  this.setState({
                    hasError: false,
                    error: undefined,
                    errorInfo: undefined,
                  })
                }
                className='px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md'
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}
